import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import BadgeIcon from '@mui/icons-material/Badge';
import SendIcon from '@mui/icons-material/Send';
import TagIcon from '@mui/icons-material/Tag';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import { Cohort } from './TableCohortsRow';
import { Team } from './TableTeamsRow';
import { Survey } from './TableSurveysRow';
import { COHORT_DATE_COUNT } from '../Constants';

export interface CohortAttachment {
  key: string;
  name: string;
}

export interface CohortDateEmailConfig {
  subject: string;
  /** HTML produced by the rich-text editor */
  body: string;
  attachments: CohortAttachment[];
  /** teamId → surveyId: which survey link to inject per team */
  teamSurveySelections?: Record<string, string>;
}

/** Keyed by date index string ("1"–"6") */
export type CohortEmailConfigs = Record<string, CohortDateEmailConfig>;

const DEFAULT_SUBJECT = '';
const DEFAULT_BODY = '<p></p>';

// ---------------------------------------------------------------------------
// Tiptap toolbar
// ---------------------------------------------------------------------------

interface ToolbarProps {
  editor: ReturnType<typeof useEditor>;
  onInsertSurveyLink?: () => void;
  onInsertTeamName?: () => void;
  onInsertResponsesCount?: () => void;
}

export const Toolbar: React.FC<ToolbarProps> = ({
  editor,
  onInsertSurveyLink,
  onInsertTeamName,
  onInsertResponsesCount,
}) => {
  const { t } = useTranslation();
  if (!editor) return null;
  const btn = (active: boolean, onClick: () => void, icon: React.ReactNode, title?: string) => (
    <IconButton
      size="small"
      title={title}
      onMouseDown={(e) => {
        e.preventDefault(); // keep editor focus
        onClick();
      }}
      sx={{ color: active ? 'primary.main' : 'text.secondary' }}
    >
      {icon}
    </IconButton>
  );
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 0.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 0.5,
        mb: 0.5,
        flexWrap: 'wrap',
      }}
    >
      {btn(
        editor.isActive('bold'),
        () => editor.chain().focus().toggleBold().run(),
        <FormatBoldIcon fontSize="small" />
      )}
      {btn(
        editor.isActive('italic'),
        () => editor.chain().focus().toggleItalic().run(),
        <FormatItalicIcon fontSize="small" />
      )}
      {btn(
        editor.isActive('underline'),
        () => editor.chain().focus().toggleUnderline().run(),
        <FormatUnderlinedIcon fontSize="small" />
      )}
      {btn(
        editor.isActive('bulletList'),
        () => editor.chain().focus().toggleBulletList().run(),
        <FormatListBulletedIcon fontSize="small" />
      )}
      {btn(
        editor.isActive('orderedList'),
        () => editor.chain().focus().toggleOrderedList().run(),
        <FormatListNumberedIcon fontSize="small" />
      )}
      {onInsertSurveyLink && (
        <Tooltip title={t('cohorts.email_insert_survey_link')} arrow>
          {btn(false, onInsertSurveyLink, <InsertLinkIcon fontSize="small" />)}
        </Tooltip>
      )}
      {onInsertTeamName && (
        <Tooltip title={t('cohorts.email_insert_team_name')} arrow>
          {btn(false, onInsertTeamName, <BadgeIcon fontSize="small" />)}
        </Tooltip>
      )}
      {onInsertResponsesCount && (
        <Tooltip title={t('cohorts.email_insert_responses_count')} arrow>
          {btn(false, onInsertResponsesCount, <TagIcon fontSize="small" />)}
        </Tooltip>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Single-date editor — mounts fresh per active tab
// ---------------------------------------------------------------------------

export interface DateEditorProps {
  initialSubject: string;
  initialBody: string;
  initialAttachments: CohortAttachment[];
  initialTeamSurveySelections?: Record<string, string>;
  cohortId: string;
  dateIndex: number | string;
  teams?: Team[];
  allSurveys?: Survey[];
  onUploadAttachment: (
    cohortId: string,
    fileName: string,
    fileType: string
  ) => Promise<{ uploadUrl: string; fileKey: string }>;
  onChange: (
    subject: string,
    body: string,
    attachments: CohortAttachment[],
    teamSurveySelections: Record<string, string>
  ) => void;
  showResponsesCount?: boolean;
}

export const DateEditor: React.FC<DateEditorProps> = ({
  initialSubject,
  initialBody,
  initialAttachments,
  initialTeamSurveySelections,
  cohortId,
  dateIndex,
  teams,
  allSurveys,
  onUploadAttachment,
  onChange,
  showResponsesCount,
}) => {
  const { t } = useTranslation();
  const [subject, setSubject] = React.useState(initialSubject);
  const [attachments, setAttachments] = React.useState<CohortAttachment[]>(initialAttachments);
  const [uploadingIdx, setUploadingIdx] = React.useState<number | null>(null);
  const [teamSurveySelections, setTeamSurveySelections] = React.useState<Record<string, string>>(
    () => {
      const defaults: Record<string, string> = {};
      (teams ?? []).forEach((team) => {
        if (team.surveyIds[0]) defaults[team.id] = team.surveyIds[0];
      });
      return { ...defaults, ...(initialTeamSurveySelections ?? {}) };
    }
  );
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Keep a ref so the tiptap onUpdate closure always reads the latest value
  const tssRef = React.useRef(teamSurveySelections);
  React.useEffect(() => {
    tssRef.current = teamSurveySelections;
  }, [teamSurveySelections]);

  const editor = useEditor({
    extensions: [StarterKit, Underline, Link.configure({ openOnClick: false })],
    content: initialBody,
    onUpdate: ({ editor: e }) => {
      onChange(subject, e.getHTML(), attachments, tssRef.current);
    },
  });

  // Propagate subject / attachment / survey-selection changes upward
  React.useEffect(() => {
    onChange(subject, editor?.getHTML() ?? initialBody, attachments, teamSurveySelections);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject, attachments, teamSurveySelections]);

  const handleInsertSurveyLink = () => {
    if (!editor) return;
    editor
      .chain()
      .focus()
      .insertContent([
        {
          type: 'text',
          text: '{{survey_link}}',
          marks: [{ type: 'link', attrs: { href: '{{survey_link}}' } }],
        },
        { type: 'text', text: ' ' },
      ])
      .run();
  };

  const handleInsertTeamName = () => {
    if (!editor) return;
    editor.chain().focus().insertContent('{{team_name}}').run();
  };

  const handleInsertResponsesCount = () => {
    if (!editor) return;
    editor.chain().focus().insertContent('{{responses_count}}').run();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const idx = attachments.length;
    setUploadingIdx(idx);
    try {
      for (const file of Array.from(files)) {
        const sanitized = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const key = `cohort-attachments/${cohortId}/${dateIndex}/${Date.now()}_${sanitized}`;
        const { uploadUrl } = await onUploadAttachment(cohortId, key, file.type);
        await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });
        setAttachments((prev) => [...prev, { key, name: file.name }]);
      }
    } finally {
      setUploadingIdx(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (key: string) => {
    setAttachments((prev) => prev.filter((a) => a.key !== key));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        label={t('cohorts.email_subject')}
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        size="small"
        fullWidth
      />

      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
          {t('cohorts.email_body')}
        </Typography>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            '& .tiptap': {
              padding: '8px 12px',
              outline: 'none',
              minHeight: 180,
              fontSize: '0.875rem',
              '& p': { margin: '2px 0' },
              '& ul, & ol': { paddingLeft: '1.5em', margin: '4px 0' },
              '& a': { color: 'primary.main' },
            },
          }}
        >
          <Box sx={{ px: 1, pt: 0.5 }}>
            <Toolbar
              editor={editor}
              onInsertSurveyLink={handleInsertSurveyLink}
              onInsertTeamName={handleInsertTeamName}
              onInsertResponsesCount={showResponsesCount ? handleInsertResponsesCount : undefined}
            />
          </Box>
          <EditorContent editor={editor} />
        </Box>
      </Box>

      <Divider />

      {/* Attachments */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t('cohorts.email_attachments')}
          </Typography>
          <Button
            size="small"
            startIcon={
              uploadingIdx !== null ? (
                <CircularProgress size={14} />
              ) : (
                <AttachFileIcon fontSize="small" />
              )
            }
            disabled={uploadingIdx !== null}
            onClick={() => fileInputRef.current?.click()}
          >
            {t('cohorts.email_add_attachment')}
          </Button>
          <input ref={fileInputRef} type="file" multiple hidden onChange={handleFileChange} />
        </Box>
        {attachments.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('cohorts.email_no_attachments')}
          </Typography>
        ) : (
          <List dense disablePadding>
            {attachments.map((a) => (
              <ListItem
                key={a.key}
                disableGutters
                secondaryAction={
                  <IconButton size="small" edge="end" onClick={() => removeAttachment(a.key)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                }
              >
                <AttachFileIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                <ListItemText primary={a.name} primaryTypographyProps={{ variant: 'body2' }} />
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      {/* Survey selection per team */}
      {teams && teams.length > 0 && (
        <>
          <Divider />
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              {t('cohorts.email_survey_selection_title')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {teams.map((team) => {
                const teamSurveys = (allSurveys ?? []).filter((s) =>
                  team.surveyIds.includes(String(s.i))
                );
                const selected = teamSurveySelections[team.id] ?? team.surveyIds[0] ?? '';
                return (
                  <Box key={team.id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" sx={{ minWidth: 140, fontWeight: 500 }} noWrap>
                      {team.name}
                    </Typography>
                    <FormControl size="small" sx={{ flex: 1 }} disabled={teamSurveys.length === 0}>
                      <Select
                        value={selected}
                        onChange={(e) =>
                          setTeamSurveySelections((prev) => ({
                            ...prev,
                            [team.id]: e.target.value,
                          }))
                        }
                        displayEmpty
                      >
                        {teamSurveys.length === 0 ? (
                          <MenuItem value="" disabled>
                            {t('cohorts.email_no_survey_for_team')}
                          </MenuItem>
                        ) : (
                          teamSurveys.map((s) => (
                            <MenuItem key={s.i} value={String(s.i)}>
                              {s.t}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Dialog
// ---------------------------------------------------------------------------

interface CohortEmailDialogProps {
  cohort: Cohort;
  open: boolean;
  onClose: () => void;
  onSave: (cohortId: string, dateIndex: string, config: CohortDateEmailConfig) => Promise<void>;
  onUploadAttachment: (
    cohortId: string,
    fileName: string,
    fileType: string
  ) => Promise<{ uploadUrl: string; fileKey: string }>;
  teams?: Team[];
  allSurveys?: Survey[];
  onSendEmails?: (
    cohortId: string,
    dateIndex: string
  ) => Promise<{ sent: number; errors: string[] }>;
}

export const CohortEmailDialog: React.FC<CohortEmailDialogProps> = ({
  cohort,
  open,
  onClose,
  onSave,
  onUploadAttachment,
  teams,
  allSurveys,
  onSendEmails,
}) => {
  const { t } = useTranslation();

  // Indices (1-based) of dates that are actually set
  const activeDateIndices = React.useMemo(
    () =>
      Array.from({ length: COHORT_DATE_COUNT }, (_, i) => i + 1).filter(
        (n) => !!(cohort[`date${n}` as keyof typeof cohort] as string | undefined)
      ),
    [cohort]
  );

  const [tabIndex, setTabIndex] = React.useState(0);
  const [saving, setSaving] = React.useState(false);
  const [confirmSendOpen, setConfirmSendOpen] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [sendResult, setSendResult] = React.useState<{ sent: number; errors: string[] } | null>(
    null
  );

  // Local state: one config entry per date index (string key) + 'reminder'
  const [configs, setConfigs] = React.useState<CohortEmailConfigs>(() => {
    const init: CohortEmailConfigs = {};
    activeDateIndices.forEach((n) => {
      init[String(n)] = cohort.emailConfigs?.[String(n)] ?? {
        subject: DEFAULT_SUBJECT,
        body: DEFAULT_BODY,
        attachments: [],
      };
    });
    init['reminder'] = cohort.emailConfigs?.['reminder'] ?? {
      subject: DEFAULT_SUBJECT,
      body: DEFAULT_BODY,
      attachments: [],
    };
    return init;
  });

  // Reset state when dialog opens for a (possibly different) cohort
  React.useEffect(() => {
    if (open) {
      setTabIndex(0);
      setSendResult(null);
      const init: CohortEmailConfigs = {};
      activeDateIndices.forEach((n) => {
        init[String(n)] = cohort.emailConfigs?.[String(n)] ?? {
          subject: DEFAULT_SUBJECT,
          body: DEFAULT_BODY,
          attachments: [],
        };
      });
      init['reminder'] = cohort.emailConfigs?.['reminder'] ?? {
        subject: DEFAULT_SUBJECT,
        body: DEFAULT_BODY,
        attachments: [],
      };
      setConfigs(init);
    }
  }, [open, cohort.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (
    dateKey: number | string,
    subject: string,
    body: string,
    attachments: CohortAttachment[],
    teamSurveySelections: Record<string, string>
  ) => {
    setConfigs((prev) => ({
      ...prev,
      [String(dateKey)]: { subject, body, attachments, teamSurveySelections },
    }));
  };

  const isReminderTab = tabIndex === activeDateIndices.length;
  const activeDateIndex = isReminderTab ? undefined : activeDateIndices[tabIndex];
  const activeDateKey = isReminderTab
    ? 'reminder'
    : activeDateIndex !== undefined
      ? String(activeDateIndex)
      : undefined;

  const handleSave = async () => {
    if (activeDateKey === undefined) return;
    const config = configs[activeDateKey];
    if (!config) return;
    setSaving(true);
    await onSave(cohort.id, activeDateKey, config);
    setSaving(false);
  };

  const handleSendEmails = async () => {
    if (!onSendEmails || activeDateKey === undefined) return;
    setSending(true);
    setConfirmSendOpen(false);
    try {
      const result = await onSendEmails(cohort.id, activeDateKey);
      setSendResult(result);
    } finally {
      setSending(false);
    }
  };

  const activeDateName =
    activeDateIndex !== undefined
      ? (cohort.dateNames?.[activeDateIndex - 1] ??
        t(`cohorts.date_default_name_${activeDateIndex}`))
      : '';

  // Rows shown in the send-confirmation dialog
  const confirmRows = React.useMemo(() => {
    if (!teams || !allSurveys || activeDateKey === undefined) return [];
    const config = configs[activeDateKey];
    return teams.map((team) => {
      const surveyId = config?.teamSurveySelections?.[team.id] ?? team.surveyIds[0];
      const survey = allSurveys.find((s) => String(s.i) === surveyId);
      return {
        team,
        surveyName: survey?.t ?? surveyId ?? '?',
        leaders: team.leader,
      };
    });
  }, [teams, allSurveys, activeDateKey, configs]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('cohorts.email_dialog_title', { name: cohort.name })}</DialogTitle>
      <DialogContent dividers>
        {sendResult && (
          <Alert
            severity={sendResult.errors.length > 0 ? 'warning' : 'success'}
            onClose={() => setSendResult(null)}
            sx={{ mb: 2 }}
          >
            {t('cohorts.email_send_result_success', { n: sendResult.sent })}
            {sendResult.errors.length > 0 && ` — ${sendResult.errors.join(', ')}`}
          </Alert>
        )}

        <>
          <Tabs
            value={tabIndex}
            onChange={(_, v) => setTabIndex(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}
          >
            {activeDateIndices.map((n) => (
              <Tab
                key={n}
                label={`${cohort.dateNames?.[n - 1] ?? t(`cohorts.date_default_name_${n}`)}: ${cohort[`date${n}` as keyof typeof cohort] as string}`}
              />
            ))}
            <Tab key="reminder" label={t('cohorts.email_reminder_tab')} />
          </Tabs>
          {isReminderTab ? (
            <DateEditor
              key="reminder"
              initialSubject={configs['reminder']?.subject ?? DEFAULT_SUBJECT}
              initialBody={configs['reminder']?.body ?? DEFAULT_BODY}
              initialAttachments={configs['reminder']?.attachments ?? []}
              initialTeamSurveySelections={configs['reminder']?.teamSurveySelections}
              cohortId={cohort.id}
              dateIndex="reminder"
              teams={teams}
              allSurveys={allSurveys}
              showResponsesCount
              onUploadAttachment={onUploadAttachment}
              onChange={(subject, body, attachments, teamSurveySelections) =>
                handleChange('reminder', subject, body, attachments, teamSurveySelections)
              }
            />
          ) : activeDateIndex !== undefined ? (
            <DateEditor
              key={activeDateIndex}
              initialSubject={configs[String(activeDateIndex)]?.subject ?? DEFAULT_SUBJECT}
              initialBody={configs[String(activeDateIndex)]?.body ?? DEFAULT_BODY}
              initialAttachments={configs[String(activeDateIndex)]?.attachments ?? []}
              initialTeamSurveySelections={configs[String(activeDateIndex)]?.teamSurveySelections}
              cohortId={cohort.id}
              dateIndex={activeDateIndex}
              teams={teams}
              allSurveys={allSurveys}
              onUploadAttachment={onUploadAttachment}
              onChange={(subject, body, attachments, teamSurveySelections) =>
                handleChange(activeDateIndex, subject, body, attachments, teamSurveySelections)
              }
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('cohorts.email_no_dates')}
            </Typography>
          )}
        </>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
        <Button
          variant="outlined"
          onClick={handleSave}
          disabled={saving || activeDateKey === undefined}
        >
          {saving ? (
            <CircularProgress size={20} />
          ) : isReminderTab ? (
            t('cohorts.email_save_reminder')
          ) : (
            t('cohorts.email_save_date', { n: activeDateName })
          )}
        </Button>
        {onSendEmails && (
          <Button
            variant="contained"
            startIcon={sending ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
            onClick={() => setConfirmSendOpen(true)}
            disabled={sending || activeDateKey === undefined}
          >
            {isReminderTab
              ? t('cohorts.email_send_reminder')
              : t('cohorts.email_send_date', { n: activeDateName })}
          </Button>
        )}
      </DialogActions>

      {/* Send confirmation dialog */}
      <Dialog
        open={confirmSendOpen}
        onClose={() => setConfirmSendOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('cohorts.email_send_confirm_title')}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('cohorts.email_send_confirm_body')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {confirmRows.map(({ team, surveyName, leaders }) => (
              <Box
                key={team.id}
                sx={{
                  display: 'flex',
                  gap: 2,
                  py: 0.75,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  alignItems: 'baseline',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, minWidth: 120, flexShrink: 0 }}
                  noWrap
                >
                  {team.name}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ flex: 1, minWidth: 0 }}
                  noWrap
                >
                  {surveyName}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ flexShrink: 0, maxWidth: 320, wordBreak: 'break-all' }}
                >
                  {leaders.join(', ')}
                </Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSendOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" startIcon={<SendIcon />} onClick={handleSendEmails}>
            {t('cohorts.email_send_n', { n: confirmRows.length })}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};
